import React from 'react';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';

import { Table, TableHeader, TableBody, TableRow, TableCell, TableHead } from '@/components/ui/table';
import { WorkActivity, WorkActivityStatus } from '@/services/types/work-activity.types';
import { useBudgetPermissions } from '@/hooks/useBudgetPermissions';
import defaultUserAvatar from '@/assets/images/default-user.png';
import workActivityPendingIcon from '@/assets/icons/work-activity-pending.svg';
import workActivityRejectedIcon from '@/assets/icons/work-activity-rejected.svg';
import workActivityAcceptedIcon from '@/assets/icons/work-activity-accepted.svg';
import {
  formatWorkActivityTableAmount,
  formatWorkActivityTableDuration,
  getWorkActivityStatusLabel
} from '@/utils/workActivityForm';

interface WorkActivityTableProps {
  workActivities: WorkActivity[];
  loading?: boolean;
  currency?: string;
  budgetId?: string;
  onWorkActivitySelect?: (workActivity: WorkActivity) => void;
}

const STATUS_ICONS: Record<WorkActivityStatus, string> = {
  PENDING: workActivityPendingIcon,
  REJECTED: workActivityRejectedIcon,
  APPROVED: workActivityAcceptedIcon
};

const WorkActivityTable = ({
  workActivities = [],
  loading = false,
  currency = 'EUR',
  budgetId,
  onWorkActivitySelect
}: WorkActivityTableProps) => {
  const { role } = useBudgetPermissions({ budgetId });
  const showStaffNameColumn = role !== 'STAFF';
  const columnCount = showStaffNameColumn ? 6 : 5;

  const handleRowClick = (workActivity: WorkActivity) => {
    onWorkActivitySelect?.(workActivity);
  };

  const renderStatus = (status: WorkActivityStatus) => (
    <div className="flex items-center gap-2">
      <img src={STATUS_ICONS[status]} alt="" className="w-4 h-4 shrink-0" />
      <span className="text-sm">{getWorkActivityStatusLabel(status)}</span>
    </div>
  );

  const renderStaffName = (workActivity: WorkActivity) => (
    <div className="flex items-center gap-2 min-w-0">
      <img
        src={workActivity.creator.profile_pic_url || defaultUserAvatar}
        alt={workActivity.creator.name}
        className="w-6 h-6 rounded-full object-cover shrink-0"
        onError={(event) => {
          event.currentTarget.src = defaultUserAvatar;
        }}
      />
      <span className="truncate">{workActivity.creator.name}</span>
    </div>
  );

  return (
    <>
      <div className="flex-1 min-w-0 hidden md:block">
        <Table className="w-full min-w-0 hidden md:table max-w-full">
          <TableHeader>
            <TableRow>
              {showStaffNameColumn && <TableHead className="text-secondary">Staff Name</TableHead>}
              <TableHead className="text-secondary">Activity</TableHead>
              <TableHead className="text-secondary">Date</TableHead>
              <TableHead className="text-secondary">Duration</TableHead>
              <TableHead className="text-secondary">Amount</TableHead>
              <TableHead className="text-secondary">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="py-10 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin text-secondary" />
                    <p className="text-sm font-medium text-secondary">Loading work activities...</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : workActivities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnCount} className="py-10 text-center">
                  <p className="text-sm font-medium text-secondary">No work activities found</p>
                </TableCell>
              </TableRow>
            ) : (
              workActivities.map((workActivity) => (
                <TableRow
                  key={workActivity._id}
                  className={`border-none hover:bg-[#DBD6FF66] rounded-[8px] ${onWorkActivitySelect ? 'cursor-pointer' : ''}`}
                  onClick={() => handleRowClick(workActivity)}
                >
                  {showStaffNameColumn && (
                    <TableCell className="text-sm rounded-tl-[8px] rounded-bl-[8px]">
                      {renderStaffName(workActivity)}
                    </TableCell>
                  )}
                  <TableCell className={`text-sm ${!showStaffNameColumn ? 'rounded-tl-[8px] rounded-bl-[8px]' : ''}`}>
                    {workActivity.activity}
                  </TableCell>
                  <TableCell className="text-sm">
                    {workActivity.activity_date ? format(new Date(workActivity.activity_date), 'd MMM yyyy') : '-'}
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatWorkActivityTableDuration(workActivity.duration_hours, workActivity.duration_minutes)}
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatWorkActivityTableAmount(workActivity.total_amount ?? null, currency)}
                  </TableCell>
                  <TableCell className="text-sm rounded-tr-[8px] rounded-br-[8px]">
                    {renderStatus(workActivity.status)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex-1 flex flex-col gap-2 md:hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10">
            <Loader2 className="w-5 h-5 animate-spin text-secondary" />
            <p className="text-sm font-medium text-secondary">Loading work activities...</p>
          </div>
        ) : workActivities.length === 0 ? (
          <div className="flex items-center justify-center py-10">
            <p className="text-sm font-medium text-secondary">No work activities found</p>
          </div>
        ) : (
          workActivities.map((workActivity) => (
            <button
              key={workActivity._id}
              type="button"
              onClick={() => handleRowClick(workActivity)}
              className="w-full flex flex-col gap-2 border-b pb-2.5 last:border-b-0 text-left"
            >
              <div className="flex items-center justify-between gap-2">
                {showStaffNameColumn ? (
                  renderStaffName(workActivity)
                ) : (
                  <span className="text-sm font-semibold">{workActivity.activity}</span>
                )}
                {renderStatus(workActivity.status)}
              </div>
              <div className="flex items-center justify-between gap-2">
                {showStaffNameColumn && <span className="text-sm font-semibold">{workActivity.activity}</span>}
                <span className={`text-sm font-semibold ${showStaffNameColumn ? '' : 'ml-auto'}`}>
                  {formatWorkActivityTableAmount(workActivity.total_amount ?? null, currency)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 text-xs text-secondary font-medium">
                <span>
                  {workActivity.activity_date ? format(new Date(workActivity.activity_date), 'd MMM yyyy') : '-'}
                </span>
                <span>
                  {formatWorkActivityTableDuration(workActivity.duration_hours, workActivity.duration_minutes)}
                </span>
              </div>
            </button>
          ))
        )}
      </div>
    </>
  );
};

export default WorkActivityTable;
